import type { CompetitionHabit } from '@/types/competitions';
import { applyStepsTarget } from '@/lib/health/stepsTarget';
import type { HabitPreset } from '@/types/habits';

/**
 * A new competition-habit id. Logs are keyed on it
 * (`competitionsLogs/{competitionId}_{habitId}_{userId}_{date}`), so it is
 * minted once when the habit is added and never changes afterwards.
 */
export function newCompetitionHabitId(now: number = Date.now(), random: () => number = Math.random): string {
  return `habit_${now}_${random().toString(36).slice(2, 11)}`;
}

/**
 * A habit-library preset (`habitPresets`) as a competition habit (namat#24).
 * Competition habits carry one name, so the preset's Arabic name is used when
 * the manager works in Arabic, as AddHabitDialog does for personal habits.
 * An empty description is left out: Firestore rejects `undefined` fields.
 */
export function presetToCompetitionHabit(preset: HabitPreset, language: string, id: string): CompetitionHabit {
  const ar = language === 'ar';
  const name = ((ar && preset.nameAr) || preset.name).trim();
  const description = ((ar && preset.descriptionAr) || preset.description || '').trim();
  const habit: CompetitionHabit = { id, name, category: preset.category };
  if (description) habit.description = description;
  return habit;
}

const sameText = (a: string, b: string) => a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();

/** Whether the list already has a habit with this name and category. */
export function hasSameHabit(habits: CompetitionHabit[], candidate: Pick<CompetitionHabit, 'name' | 'category'>): boolean {
  return habits.some(h => h.category === candidate.category && sameText(h.name, candidate.name));
}

/** The form fields of a competition habit being edited (namat#25). */
export interface CompetitionHabitEdit {
  name: string;
  category: CompetitionHabit['category'];
  description: string;
  stepsTarget: string;
}

/**
 * Apply an edit to a habit already in a competition (namat#25).
 *
 * - The id never changes: every competitionsLogs doc (and its doc id
 *   `{competitionId}_{habitId}_{userId}_{date}`) points at it, so a new id
 *   would orphan the habit's logs and zero its tallies.
 * - Name, description and steps target are free to change: rankings,
 *   tallies and steps auto-logging find a habit by its id, not its name.
 * - The category is locked once the habit has logs, because each log keeps
 *   the category it was logged under (`habitCategory`) and the manager's
 *   statistics count by that — a later change would split one habit across
 *   two categories.
 */
export function applyHabitEdit(
  habit: CompetitionHabit,
  edit: CompetitionHabitEdit,
  { categoryLocked }: { categoryLocked: boolean }
): CompetitionHabit {
  const name = edit.name.trim() || habit.name;
  const description = edit.description.trim();
  const next: CompetitionHabit = {
    ...habit,
    id: habit.id,
    name,
    category: categoryLocked ? habit.category : edit.category,
  };
  if (description) next.description = description;
  else delete next.description;
  return applyStepsTarget(next, edit.stepsTarget);
}
